import { basicAuth, headerAuth } from "../standardAuthPatterns";
import { defineProduct, productVariant } from "../types";
import { exactUrlToRegex } from "../urlPatternConversion";
import logo from "./logo.svg";

const keywords = "firewall router netgate pfrest vpn nat dhcp network";

const defaultUrlPattern = ({ instanceUrl }: Record<"instanceUrl", string>) =>
  instanceUrl ? exactUrlToRegex(`${instanceUrl}/api`) : "";

const packageStep = {
  title: "Install the REST API package",
  items: [
    {
      kind: "markdown",
      content:
        "pfSense doesn’t ship with a REST API — install the open-source [REST API package](https://pfrest.org/INSTALL_AND_CONFIG/) on your firewall first.",
    },
  ],
} as const;

const serverUrlStep = {
  title: "Enter your pfSense details",
  items: [
    {
      kind: "baseUrl",
      label: "Server URL",
      required: true,
      placeholder: "https://pfsense.example.com",
      target: { kind: "configuration", field: "instanceUrl" },
      helperText: "The base URL of your pfSense webConfigurator.",
    },
  ],
} as const;

const connectionTest = {
  protocol: "http" as const,
  request: ({ instanceUrl }: Record<"instanceUrl", string>) =>
    `${instanceUrl}/api/v2/system/version`,
};

const usageHint = () => "Use pfSense REST API v2 endpoints under /api/v2/.";

export const pfsense = defineProduct({
  name: "pfSense",
  tagLine: "Open-source firewall and router",
  logo,
  tags: ["security"],
  variants: [
    productVariant({
      identifier: "pfsense",
      authLabel: "API key",
      keywords,
      protocol: "http",
      defaultUrlPattern,
      auth: (app) =>
        headerAuth([{ name: "X-API-Key", value: app.secret.apiKey }]),
      steps: [
        packageStep,
        {
          title: "Create an API key",
          items: [
            {
              kind: "markdown",
              content:
                "In the pfSense webConfigurator, go to **System → REST API → Settings** and add **Key** to the allowed authentication methods — only Basic authentication is enabled by default. Then go to **System → REST API → Keys** and generate a new key. The key carries the privileges of the user who created it.",
            },
            {
              kind: "input",
              label: "API key",
              required: true,
              target: { kind: "secrets", field: "apiKey" },
            },
          ],
        },
        serverUrlStep,
      ],
      connectionTest,
      usageHint,
    }),
    productVariant({
      identifier: "pfsenseBasicAuth",
      authLabel: "Username and password",
      keywords,
      protocol: "http",
      defaultUrlPattern,
      auth: (app) =>
        basicAuth({
          username: app.config.username,
          password: app.secret.password,
        }),
      steps: [
        packageStep,
        {
          title: "Enter your credentials",
          items: [
            {
              kind: "markdown",
              content:
                "Use the username and password of a local pfSense user with API privileges. The REST API doesn’t support LDAP or RADIUS users for Basic authentication.",
            },
            {
              kind: "input",
              label: "Username",
              required: true,
              placeholder: "admin",
              target: { kind: "configuration", field: "username" },
            },
            {
              kind: "input",
              label: "Password",
              required: true,
              target: { kind: "secrets", field: "password" },
            },
          ],
        },
        serverUrlStep,
      ],
      connectionTest,
      usageHint,
    }),
  ],
});
